import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

const nextMondayAt = (hour = 16, minute = 0) => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const daysToMon = (8 - day) % 7 || 7;
  const target = new Date(now);
  target.setDate(now.getDate() + daysToMon);
  target.setHours(hour, minute, 0, 0);
  return target;
};

const formatNice = d =>
  d.toLocaleString("en-GB", {
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });

const LeaveForHeathrowLite = () => {
  const [scenario, setScenario] = useState("DEPARTURE");
  const [terminal, setTerminal] = useState("T2");
  const [flightType, setFlightType] = useState("longhaul");
  const [depTime, setDepTime] = useState(
    nextMondayAt().toISOString().slice(0, 16)
  );

  const calcDepartureBuffer = () => {
    let base = 150;
    if (flightType === "domestic") base = 90;
    if (flightType === "europe") base = 120;
    let adj = 0;
    if (terminal === "T5") adj = 10;
    else if (terminal === "T4") adj = 5;
    return base + adj;
  };

  const calcArrivalWindow = () => {
    let min = 45;
    let max = 90;
    if (flightType === "domestic") {
      min = 15;
      max = 30;
    } else if (flightType === "europe") {
      min = 25;
      max = 60;
    }
    if (terminal === "T4") {
      min += 15;
      max += 15;
    } else if (terminal === "T5") {
      min += 5;
      max += 5;
    }
    return { min, max };
  };

  const result = useMemo(() => {
    const scheduled = new Date(depTime);
    if (Number.isNaN(scheduled.getTime())) return null;

    if (scenario === "DEPARTURE") {
      const buffer = calcDepartureBuffer();
      const arriveBy = new Date(scheduled.getTime() - buffer * 60000);
      return {
        arriveBy,
        bufferMinutes: buffer,
        scheduled
      };
    }

    const { min, max } = calcArrivalWindow();
    const readyMin = new Date(scheduled.getTime() + min * 60000);
    const readyMax = new Date(scheduled.getTime() + max * 60000);
    return {
      readyMin,
      readyMax,
      scheduled
    };
  }, [depTime, scenario, flightType, terminal]);

  const queueEstimate = useMemo(() => {
    const scheduled = new Date(depTime);
    if (Number.isNaN(scheduled.getTime())) return null;

    const hour = scheduled.getHours();
    const band =
      hour >= 4 && hour < 8
        ? { label: "Early morning (04:00–07:59)", security: [10, 25], immigration: [15, 30] }
        : hour >= 8 && hour < 12
        ? { label: "Morning peak (08:00–11:59)", security: [20, 45], immigration: [20, 45] }
        : hour >= 12 && hour < 16
        ? { label: "Midday (12:00–15:59)", security: [15, 35], immigration: [25, 55] }
        : hour >= 16 && hour < 21
        ? { label: "Evening peak (16:00–20:59)", security: [20, 40], immigration: [35, 75] }
        : { label: "Late (21:00–03:59)", security: [10, 25], immigration: [20, 45] };

    const adjust = ([min, max]) => {
      let adjMin = min;
      let adjMax = max;
      if (terminal === "T5") adjMax += 5;
      if (terminal === "T4" && scenario === "ARRIVAL_PICKUP") {
        adjMin += 5;
        adjMax += 10;
      }
      return [adjMin, adjMax];
    };

    const [min, max] =
      scenario === "DEPARTURE" ? adjust(band.security) : adjust(band.immigration);

    const startHour = (hour + 23) % 24;
    const endHour = (hour + 1) % 24;
    const fmtHr = h => `${String(h).padStart(2, "0")}:00`;

    return {
      band: band.label,
      min,
      max,
      snapshotWindow: `${fmtHr(startHour)}–${fmtHr(endHour)}`
    };
  }, [depTime, scenario, terminal]);

  return (
    <>
      <Helmet>
        <title>How Early Should I Arrive at Heathrow? | Lite Guide 2026</title>
        <meta
          name="description"
          content="Quick guide for Heathrow arrival timings. Pick terminal, flight type and departure time to see when to arrive. Upgrade to the full calculator for postcode-based leave times."
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/how-early-to-arrive-heathrow"
        />
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-wrapper">
        <div className="parking-header">
          <h1>How Early Should I Arrive at Heathrow?</h1>
          <p>
            Instant Heathrow timing guidance by terminal and flight type. For personalised
            postcode-based leave times, upgrade to the main tool.
          </p>
          <p style={{ fontSize: 12, color: "#475467" }}>Last Updated: 14 Jan 2026</p>
        </div>

        {/* Inputs */}
        <div className="parking-form">
          <div className="form-group">
            <label>Journey type</label>
            <div className="option-grid option-grid-2">
              <label className="option-card">
                <input
                  type="radio"
                  name="scenario"
                  value="DEPARTURE"
                  checked={scenario === "DEPARTURE"}
                  onChange={e => setScenario(e.target.value)}
                />
                <div className="option-text">
                  <span className="option-title">Departure</span>
                  <span className="option-sub">Default: when you are flying out.</span>
                </div>
              </label>
              <label className="option-card">
                <input
                  type="radio"
                  name="scenario"
                  value="ARRIVAL_PICKUP"
                  checked={scenario === "ARRIVAL_PICKUP"}
                  onChange={e => setScenario(e.target.value)}
                />
                <div className="option-text">
                  <span className="option-title">Arrival / Pickup</span>
                  <span className="option-sub">When meeting someone landing.</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Terminal</label>
            <select
              className="parking-input"
              value={terminal}
              onChange={e => setTerminal(e.target.value)}
            >
              <option value="T2">Terminal 2</option>
              <option value="T3">Terminal 3</option>
              <option value="T4">Terminal 4</option>
              <option value="T5">Terminal 5</option>
            </select>
          </div>

          <div className="form-group">
            <label>Flight type</label>
            <select
              className="parking-input"
              value={flightType}
              onChange={e => setFlightType(e.target.value)}
            >
              <option value="domestic">Domestic (UK)</option>
              <option value="europe">Europe</option>
              <option value="longhaul">International / Long-haul</option>
            </select>
          </div>

          <div className="form-group">
            <label>{scenario === "DEPARTURE" ? "Departure time" : "Landing time"}</label>
            <input
              type="datetime-local"
              className="parking-input"
              value={depTime}
              onChange={e => setDepTime(e.target.value)}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Prefilled to next Monday 16:00 for a realistic example.
            </div>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="parking-results">
            <div className="leave-hero-card">
              <div className="leave-hero-title">
                {scenario === "DEPARTURE" ? "Arrive at Heathrow by" : "Arrival readiness window"}
              </div>
              <div className="leave-hero-time">
                {scenario === "DEPARTURE"
                  ? formatNice(result.arriveBy)
                  : `${formatNice(result.readyMin)} – ${formatNice(result.readyMax)}`}
              </div>
              <div className="leave-hero-sub">
                {scenario === "DEPARTURE"
                  ? `For a ${flightType === "longhaul" ? "long-haul" : flightType === "europe" ? "European" : "domestic"
                    } departure from ${terminal} at ${formatNice(result.scheduled)}`
                  : `Passenger likely ready after immigration + bags for ${terminal} arrival at ${formatNice(result.scheduled)}`}
              </div>
              {scenario === "ARRIVAL_PICKUP" && (
                <div
                  style={{
                    fontSize: 13,
                    color: "#f8fafc",
                    marginTop: 6,
                    lineHeight: 1.5,
                    textShadow: "0 1px 2px rgba(0,0,0,0.35)"
                  }}
                >
                  {flightType === "longhaul" && "Passenger typically ready 45–90 minutes after landing. In busy periods this may extend up to 2 hours."}
                  {flightType === "europe" && "Passenger usually ready 30–60 minutes after landing."}
                  {flightType === "domestic" && "Passenger usually ready 15–30 minutes after landing (no immigration; baggage reclaim only)."}
                  {" Includes: taxi-in, immigration queues, baggage reclaim, terminal walking."}
                  {terminal === "T4" && " Terminal 4 arrivals often take longer due to immigration hall layout and shuttle connections."}
                </div>
              )}
            </div>

            {queueEstimate && (
              <div className="leave-section-card">
                <h3 className="leave-section-title">Queue snapshot for this time</h3>
                <p style={{ marginBottom: 6, lineHeight: 1.5 }}>
                  {scenario === "DEPARTURE"
                    ? `Typical security queues: ${queueEstimate.min}–${queueEstimate.max} mins (band: ${queueEstimate.band}).`
                    : `Typical immigration queues: ${queueEstimate.min}–${queueEstimate.max} mins (band: ${queueEstimate.band}).`}
                </p>
                <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                  Window checked: {queueEstimate.snapshotWindow}. Based on recent Heathrow snapshot patterns by terminal.
                </p>
              </div>
            )}

            <div className="leave-section-card">
              <h3 className="leave-section-title">Official guidance</h3>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Long-haul international: arrive 3 hours before departure.</li>
                <li>European: arrive 2 hours before departure.</li>
                <li>Please avoid arriving excessively early to reduce terminal congestion.</li>
                {terminal === "T5" && (
                  <li>Terminal 5: British Airways enforces a 35-minute security cut-off before departure.</li>
                )}
                {terminal === "T4" && (
                  <li>Terminal 4 rail users: allow 20–30 extra minutes for the Heathrow Central transfer.</li>
                )}
              </ul>
            </div>

            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">🧠 How we worked this out</summary>
              <div className="leave-explainer leave-collapsible-body" style={{ lineHeight: 1.6 }}>
                <p>Departure buffers include airline check-in guidance, typical security time for this terminal, and a small boarding cut-off buffer.</p>
                <p>Arrival buffers include taxiing, disembarkation, immigration and baggage reclaim typical ranges, plus terminal-specific walking/transfer time.</p>
                <p>Peak-time congestion and school holidays can increase queues; arrive earlier if you prefer a stress-free experience.</p>
              </div>
            </details>

            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">❓ FAQs</summary>
              <ul className="leave-explainer leave-collapsible-body" style={{ marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li><strong>Is 2 hours enough for a British Airways flight in 2026?</strong> For European flights, yes. For long-haul, plan 3 hours because T5 “B” gates require a shuttle train adding ~20 minutes.</li>
                <li><strong>Has the drop-off charge changed?</strong> Yes. From 1 Jan 2026 the drop-off charge is £7 per visit with a 10-minute max stay.</li>
                <li><strong>Does Terminal 4 need extra time on trains?</strong> Yes. Add 20–30 minutes for the Heathrow Central transfer when using Elizabeth Line or HEX.</li>
              </ul>
            </details>

            <div style={{ marginTop: 18, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
              <strong>Upgrade for a personalised leave-home time</strong>
              <p style={{ marginTop: 6, marginBottom: 6, lineHeight: 1.5 }}>
                Enter your postcode, travel mode, bags and passengers to get a door-to-terminal plan with live-style buffers.
              </p>
              <a href="/leave-for-heathrow" className="parking-btn" style={{ display: "inline-block", textDecoration: "none" }}>
                Go to the full calculator
              </a>
            </div>

            <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
              <strong>Plan your Heathrow journey:</strong>
              <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
                <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
                <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
                <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
                <li>✈️ <a href="/flight-checker">Check your flight</a></li>
                <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
                <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
                <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
              </ul>
            </div>
          </div>
        )}

        <div style={{ marginTop: 40 }}>
          <ReviewsBar />
        </div>
      </div>
    </>
  );
};

export default LeaveForHeathrowLite;
